import { useEffect, useRef, useState } from "react";
import { DateRange } from "react-date-range";
import { FaRegCalendarAlt } from "react-icons/fa";
import { HiChevronDown } from "react-icons/hi";
import moment from "moment";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { getDateRangeFromFilter } from "../../../utils/reportDashboard/reportDashboardUtils";

const PRESET_RANGES = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "bi-annually", label: "Bi-Annually" },
  { value: "annually", label: "Annually" },
];

const formatApiDate = (date) => moment(date).format("YYYY-MM-DD");

const formatDisplayDate = (date) =>
  date ? moment(date).format("MMM DD, YYYY") : "";

export function DateRangeFilter({ onChange }) {
  const [isPresetOpen, setIsPresetOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [timeRange, setTimeRange] = useState("monthly");
  const [customRange, setCustomRange] = useState(null);
  const calendarRef = useRef(null);

  const [selectedRange, setSelectedRange] = useState({
    startDate: moment().subtract(1, "month").toDate(),
    endDate: new Date(),
    key: "selection",
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setIsCalendarOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const emitRange = (nextRange) => {
    onChange?.(nextRange);
  };

  const handlePresetChange = (rangeValue) => {
    setTimeRange(rangeValue);
    setCustomRange(null);
    setIsPresetOpen(false);
    setIsCalendarOpen(false);
    emitRange(getDateRangeFromFilter(rangeValue));
  };

  const applyCustomRange = (startDate, endDate) => {
    const start = moment(startDate);
    const end = moment(endDate);

    if (!start.isValid() || !end.isValid()) return;

    const nextRange = start.isAfter(end)
      ? { startDate: formatApiDate(end), endDate: formatApiDate(start) }
      : { startDate: formatApiDate(start), endDate: formatApiDate(end) };

    setCustomRange(nextRange);
    setIsCalendarOpen(false);
    emitRange(nextRange);
  };

  const handleSelect = (ranges) => {
    const { startDate, endDate } = ranges.selection;

    setSelectedRange({
      startDate,
      endDate,
      key: "selection",
    });

    if (startDate && endDate && !moment(startDate).isSame(endDate, "day")) {
      applyCustomRange(startDate, endDate);
    }
  };

  const handleApplySameDay = () => {
    applyCustomRange(selectedRange.startDate, selectedRange.endDate);
  };

  const openCalendar = () => {
    setIsPresetOpen(false);
    setSelectedRange({
      startDate: customRange?.startDate
        ? moment(customRange.startDate, "YYYY-MM-DD").toDate()
        : moment().subtract(1, "month").toDate(),
      endDate: customRange?.endDate
        ? moment(customRange.endDate, "YYYY-MM-DD").toDate()
        : new Date(),
      key: "selection",
    });
    setIsCalendarOpen((open) => !open);
  };

  const currentLabel = customRange
    ? "Custom"
    : PRESET_RANGES.find((range) => range.value === timeRange)?.label ||
      "Monthly";

  const customLabel =
    customRange?.startDate && customRange?.endDate
      ? `${formatDisplayDate(customRange.startDate)} - ${formatDisplayDate(
          customRange.endDate,
        )}`
      : "Date range";

  return (
    <div className="flex items-center gap-2">
      <div className="relative">
        <button
          type="button"
          onClick={() => {
            setIsCalendarOpen(false);
            setIsPresetOpen((open) => !open);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 shadow-sm transition-colors whitespace-nowrap"
        >
          <FaRegCalendarAlt size={16} className="text-[#6B7280]" />
          <span className="text-[#1A1A1A]">{currentLabel}</span>
          <HiChevronDown size={16} className="text-[#6B7280]" />
        </button>

        {isPresetOpen && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setIsPresetOpen(false)}
            />
            <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-20">
              {PRESET_RANGES.map((range) => (
                <button
                  type="button"
                  key={range.value}
                  onClick={() => handlePresetChange(range.value)}
                  className={`w-full px-4 py-2 text-left hover:bg-gray-50 transition-colors ${
                    !customRange && timeRange === range.value
                      ? "bg-[#F8F9FC] text-[#1E61DC]"
                      : "text-[#1A1A1A]"
                  }`}
                >
                  {range.label}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="relative" ref={calendarRef}>
        <button
          type="button"
          onClick={openCalendar}
          className={`flex items-center gap-2 px-4 py-2 bg-white border rounded-lg hover:bg-gray-50 shadow-sm transition-colors whitespace-nowrap ${
            customRange
              ? "border-[#1E61DC] text-[#1E61DC]"
              : "border-gray-200 text-[#1A1A1A]"
          }`}
        >
          <FaRegCalendarAlt
            size={16}
            className={customRange ? "text-[#1E61DC]" : "text-[#6B7280]"}
          />
          <span>{customLabel}</span>
        </button>

        {isCalendarOpen && (
          <div className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
            <DateRange
              ranges={[selectedRange]}
              onChange={handleSelect}
              showDateDisplay={false}
              moveRangeOnFirstSelection={false}
              maxDate={new Date()}
              rangeColors={["#1E61DC"]}
            />
            <div className="flex items-center justify-end gap-2 px-3 py-2 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setIsCalendarOpen(false)}
                className="px-3 py-1.5 text-sm text-[#6B7280] hover:bg-gray-50 rounded-md"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApplySameDay}
                className="px-3 py-1.5 text-sm text-white bg-[#1E61DC] hover:bg-[#1A56C4] rounded-md"
              >
                Apply
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
